// MenuItems.ts
import type { MenuProps } from "antd"

// Define interfaces for menu item structure
interface BaseMenuItem {
  label: string
  key: string
}

interface SubMenuItem extends BaseMenuItem {
  children: MenuItem[]
}

interface QuestionTypeChild {
  label: string
  key: string
}

interface QuestionType {
  label: string
  key: string
  children: QuestionTypeChild[]
}

// Union type for menu items
type MenuItem = BaseMenuItem | SubMenuItem

/**
 * Generates a list of question submenus.
 *
 * @param subItems - List of sub-items.
 * @returns Generated question submenus.
 */
const generateQuestionSubmenus = (subItems: QuestionType[]): SubMenuItem[] => {
  return subItems.map(
    (item: QuestionType): SubMenuItem => ({
      label: item.label,
      key: item.key,
      children: item.children.map(
        (child: QuestionTypeChild): BaseMenuItem => ({
          label: child.label,
          key: `${item.key}_${child.key}`,
        })
      ),
    })
  )
}

const questionTypes: QuestionType[] = [
  {
    label: "Single Select",
    key: "single_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Multiple Select",
    key: "multiple_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Grid Single Select",
    key: "grid_single_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
  {
    label: "Grid Multiple Select",
    key: "grid_multiple_select",
    children: [
      { label: "Basic", key: "basic" },
      { label: "User Predefined List", key: "user_predefined_list" },
      {
        label: "Extended With Predefined List",
        key: "extended_with_predefined_list",
      },
    ],
  },
]

/**
 * Menu items configuration for the application.
 */
const items: MenuProps["items"] = [
  {
    label: "File",
    key: "file",
    children: [
      {
        label: "Convert Questionnaire using AI",
        key: "convert_questionnaire_ai",
      },
      { label: "Export Questionnaire", key: "export_questionnaire" },
      // { label: "New Variant", key: "new_variant" },
      { label: "Export to Decipher XML", key: "export_to_decipher_xml" },
      // {
      //   type: "submenu",
      //   label: "Translations",
      //   key: "translations",
      //   children: [
      //     {
      //       label: "Download Translations File",
      //       key: "download_translations_file",
      //     },
      //     {
      //       label: "Upload Translations File",
      //       key: "upload_translations_file",
      //     },
      //   ],
      // },
      {
        label: "Export Data Map",
        key: "export_data_map",
      },
      {
        label: "Fill export map from source document…",
        key: "fill_export_map_source",
      },
    ],
  },
  {
    label: "Insert",
    key: "insert",
    children: [
      // { label: "Include Instructions", key: "include_instructions" },
      { label: "Note", key: "note" },
      { label: "Section", key: "section" },
      { label: "Text", key: "text" },
      {
        label: "List",
        key: "list",
        children: [
          { label: "Simple List", key: "simple_list" },
          {
            label: "List With Various Representations",
            key: "list_with_various_representations",
          },
          { label: "Exception List", key: "exception_list" },
        ],
      },
      {
        label: "Question",
        key: "question",
        children: [
          {
            label: "Fill-in",
            key: "fill_in",
            children: [
              { label: "Number", key: "fill_in_number" },
              { label: "Text", key: "fill_in_text" },
            ],
          },
          ...generateQuestionSubmenus(questionTypes).map(item => ({ ...item, type: undefined })),
          { label: "Numerical Grid", key: "numerical_grid" },
        ],
      },
      {
        label: "Special",
        key: "special",
        children: [
          { label: "Hidden Question", key: "hidden_question" },
          { label: "Data", key: "data" },
          { label: "Recode", key: "recode" },
          {
            label: "Qnr Changes in a Variant",
            key: "qnr_changes_in_a_variant",
            children: [
              { label: "Hide a Question", key: "hide_a_question" },
              { label: "Extend a List", key: "extend_a_list" },
              { label: "Trim a List", key: "trim_a_list" },
              { label: "Reposition Questions", key: "reposition_questions" },
            ],
          },
        ],
      },
      { label: "Template", key: "template" },
    ],
  },
]

export default items
